import { useToast } from '@chakra-ui/react';
import { RightHeader, WalletConflictModal } from '@components';
import { ConnectWalletButton } from './ConnectWalletButton';
import { selectCurrentUser, setUser, setToken } from '@features/auth/authSlice';
import { selectWallet, setWallet } from '@features/wallet/walletSlice';
import { useAppDispatch, useAppSelector, useAuth } from '@hooks';
import { useChainManager } from '../../chain/hooks/useChainManager';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetMeQuery, useSignUpWithoutInfoQuery } from '@services';
import React, { useEffect, useCallback, useRef } from 'react';

export interface RightHeaderContainerProps {
  showButton?: boolean;
}

export const RightHeaderContainer: React.FC<RightHeaderContainerProps> = ({
  showButton,
}) => {
  const {
    currentChain,
    connectWallet,
    disconnectWallet,
    wallet: chainWallet,
    showWalletConflictModal,
    conflictTargetChain,
    setShowWalletConflictModal,
  } = useChainManager();
  const dispatch = useAppDispatch();
  const toast = useToast({
    duration: 2000,
    isClosable: true,
    position: 'bottom',
  });

  const { signIn, signOut, hasLogin, token } = useAuth();
  const user = useAppSelector(selectCurrentUser);
  const tokenInRedux = useAppSelector(state => state.auth.token);

  // Track if we've already cleared user due to wallet mismatch to prevent infinite loop
  const clearedDueToMismatchRef = useRef<string | null>(null);

  // User logic
  const {
    error: getMeError,
    refetch,
    isLoading: isLoadingUser,
  } = useGetMeQuery(undefined, {
    skip: !token,
    refetchOnMountOrArgChange: true,
  });

  // Debug logging
  useEffect(() => {
    console.log('RightHeaderContainer: Token and user state:', {
      hasToken: !!token,
      hasUser: !!user,
      isLoadingUser,
      getMeError: getMeError?.message,
    });
  }, [token, user, isLoadingUser, getMeError]);

  const { error: signupError, refetch: refetchSignUp } =
    useSignUpWithoutInfoQuery(
      getMeError?.message?.includes('Invalid token') ? undefined : skipToken,
    );

  // Debug logging for authentication state
  useEffect(() => {
    console.log('RightHeaderContainer: Auth state changed:', {
      hasLogin,
      token: !!token,
      user: !!user,
      chainWallet: !!chainWallet,
      userEmail: user?.email,
      walletAddress: chainWallet?.address,
    });
  }, [hasLogin, token, user, chainWallet]);

  useEffect(() => {
    (async () => {
      if (signupError) {
        const name = signupError.name || 'Error';
        const message = signupError.message || 'Something went wrong';

        return toast({
          title: name,
          description: message,
          status: 'error',
        });
      }

      if (getMeError?.message?.includes('Token expired')) {
        return signOut();
      }

      if (getMeError?.message?.includes('Invalid token')) {
        refetchSignUp();
      }
    })();
  }, [getMeError, signupError]);

  useEffect(() => {
    const tkInLocalStorage = window.localStorage.getItem('wallet_signed_token');
    if (!tokenInRedux && tkInLocalStorage) {
      console.log('RightHeaderContainer: Restoring token from localStorage');
      dispatch(setToken(tkInLocalStorage));
    }
  }, []);

  // Check wallet address mismatch and clear user if needed
  useEffect(() => {
    // Only check wallet address mismatch if both user and chainWallet have addresses
    if (!user || !user.walletAddress || !chainWallet?.address) {
      // Reset the ref if user or wallet is cleared
      if (!user || !chainWallet?.address) {
        clearedDueToMismatchRef.current = null;
      }
      return;
    }

    const currentWalletAddress = chainWallet.address.toLowerCase();
    const userWalletAddress = user.walletAddress.toLowerCase();

    // Only clear user if wallet addresses don't match AND we haven't already cleared for this wallet
    if (
      userWalletAddress !== currentWalletAddress &&
      clearedDueToMismatchRef.current !== currentWalletAddress
    ) {
      console.log(
        'RightHeaderContainer: Wallet address mismatch, clearing user',
      );
      clearedDueToMismatchRef.current = currentWalletAddress;
      dispatch(setUser(null));
    } else if (userWalletAddress === currentWalletAddress) {
      // Reset ref if addresses match (user reconnected with correct wallet)
      clearedDueToMismatchRef.current = null;
    }
  }, [user, chainWallet, dispatch]);

  // Refetch when token is available but user is not (only if not cleared due to mismatch)
  useEffect(() => {
    if (
      token &&
      !user &&
      !getMeError &&
      !isLoadingUser &&
      !clearedDueToMismatchRef.current
    ) {
      console.log(
        'RightHeaderContainer: Token available but no user, refetching getMe',
      );
      refetch();
    }
  }, [token, user, getMeError, refetch, isLoadingUser]);

  // Use chain manager wallet as single source of truth
  const currentWallet = chainWallet;

  const handleConnectWallet = useCallback(async () => {
    try {
      // signIn will handle both Solana and BNB chains
      await signIn();
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      toast({
        title: 'Connection Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
      });
    }
  }, [signIn, toast]);

  if (!hasLogin) {
    return (
      <ConnectWalletButton
        showButton={showButton}
        onConnect={handleConnectWallet}
      />
    );
  }

  return (
    <>
      <RightHeader user={user} wallet={currentWallet} />
      <WalletConflictModal
        isOpen={showWalletConflictModal}
        onClose={() => setShowWalletConflictModal(false)}
        targetChain={conflictTargetChain}
      />
    </>
  );
};

export default RightHeaderContainer;
